import { useMemo } from 'react';
import { AlignJustify, CheckSquare, FileText, Sparkles } from 'lucide-react';
import type {
    CvScreeningResult,
    ReviewerScoreEdit,
} from '../../types';
import { effectiveScoreFor } from '../../types';
import { EditableScore } from '../EditableScore';

const RANK_SCALE_LABELS = ['Unfit', 'Weak', 'Moderate', 'Good', 'Strong'];

export interface CVScreeningTabProps {
    result: CvScreeningResult;
    jobId?: string;
    taskId?: string;
    onEdited?: (edit: ReviewerScoreEdit) => void;
}

// Pure verdict from the score (when no requirement vetoes). Mirrors the
// hero verdict in CandidateReport. Hard-requirement fail forces 'Not a fit'
// regardless of score.
/** Map raw verdict strings (STRONG_FIT, FAIL, REVIEW) to display labels. */
function humanizeVerdict(v: string): string {
    const upper = v.toUpperCase();
    if (upper === 'STRONG_FIT' || upper === 'PASS') return 'Strong match';
    if (upper === 'FAIL' || upper === 'NOT_A_FIT' || upper === 'REJECT') return 'Not a fit';
    if (upper === 'REVIEW' || upper === 'NEEDS_REVIEW' || upper === 'PARKING_LOT') return 'Needs review';
    return v;
}

function fitVerdict(pct: number, anyRequirementFailed: boolean): string {
    if (anyRequirementFailed) return 'Not a fit';
    if (pct >= 85) return 'Strong match';
    if (pct >= 70) return 'Match';
    if (pct >= 55) return 'Needs review';
    if (pct >= 1) return 'Not a fit';
    return 'Pending';
}

/** Per-criterion effective verdict — prefer doc.effectiveVerdict, else AI fallback. */
function effBoolVerdict(c: { effectiveVerdict?: boolean | null; aiVerdict?: boolean | null; isFulfilled: boolean }): boolean {
    if (typeof c.effectiveVerdict === 'boolean') return c.effectiveVerdict;
    if (typeof c.aiVerdict === 'boolean') return c.aiVerdict;
    return c.isFulfilled;
}

/** Per-component effective score — prefer doc.effectiveScore, else AI fallback. */
function effNumScore(c: { effectiveScore?: number | null; aiScore?: number | null; score: number }): number {
    if (typeof c.effectiveScore === 'number') return c.effectiveScore;
    if (typeof c.aiScore === 'number') return c.aiScore;
    return c.score;
}

export function CVScreeningTab({ result, jobId, taskId, onEdited }: CVScreeningTabProps) {
    const requirements = result.criteriaResult?.criteria ?? [];
    const requirementsTotal = result.criteriaResult?.criteriaTotal ?? requirements.length;

    const components = result.profileStrength?.components ?? [];

    const requirementsMetEffective = useMemo(() => {
        let met = 0;
        for (const r of requirements) {
            // Prefer per-criterion effective fields written by the cascade,
            // fall back to legacy reviewerFeedback[] for older rows.
            const eff = effBoolVerdict(r);
            const edit = effectiveScoreFor(result.reviewerFeedback, r.criterionId);
            const fulfilled = edit ? edit.editedScore === 1 : eff;
            if (fulfilled) met += 1;
        }
        return met;
    }, [requirements, result.reviewerFeedback]);

    const componentScoreEffective = useMemo(() => {
        let total = 0;
        let max = 0;
        for (const c of components) {
            const eff = effNumScore(c);
            const edit = effectiveScoreFor(result.reviewerFeedback, c.componentId);
            total += edit ? edit.editedScore : eff;
            max += c.scoreMax;
        }
        return { total, max };
    }, [components, result.reviewerFeedback]);

    // Score = profile strength % only. Requirements are gates, not part of
    // the numeric score. Server's `effectiveScore` wins when present.
    const headlineScore = useMemo(() => {
        if (typeof result.effectiveScore === 'number') return result.effectiveScore;
        const { total, max } = componentScoreEffective;
        return max === 0 ? 0 : Math.round((total / max) * 100);
    }, [result.effectiveScore, componentScoreEffective]);

    const anyRequirementFailed = useMemo(
        () => requirements.some((r) => effBoolVerdict(r) === false),
        [requirements],
    );

    // Use server's effective verdict if it's a fail/review state and we know
    // it's been recomputed; otherwise compute locally so requirement-fail
    // propagates to "Not a fit" even before the cascade runs.
    const verdict = useMemo(() => {
        if (anyRequirementFailed) return 'Not a fit';
        if (result.effectiveVerdict) return humanizeVerdict(result.effectiveVerdict);
        if (result.verdict) return humanizeVerdict(result.verdict);
        return fitVerdict(headlineScore, anyRequirementFailed);
    }, [result.effectiveVerdict, result.verdict, headlineScore, anyRequirementFailed]);

    return (
        <div className="flex flex-col">
            {/* Hero */}
            <div className="px-5 py-5" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div
                            className="text-[10.5px] font-semibold uppercase tracking-[0.08em] mb-1.5"
                            style={{ color: 'var(--text-tertiary)' }}
                        >
                            CV Screening Score
                        </div>
                        <div className="flex items-center gap-2">
                            <span
                                className="text-[42px] leading-none tabular-nums"
                                style={{
                                    fontFamily: 'var(--v2-font-serif)',
                                    fontWeight: 500,
                                    color: 'var(--text-primary)',
                                }}
                            >
                                {headlineScore}
                            </span>
                            <span
                                className="text-[14px] tabular-nums"
                                style={{
                                    color: 'var(--text-tertiary)',
                                    fontFamily: 'var(--v2-font-serif)',
                                }}
                            >
                                / 100
                            </span>
                        </div>
                        <div
                            className="text-[13.5px] mt-2 inline-flex items-center gap-1.5 whitespace-nowrap"
                            style={{ color: 'var(--text-secondary)' }}
                        >
                            <Sparkles size={12} strokeWidth={2} style={{ color: 'var(--accent)' }} />
                            Verdict: <strong style={{ color: 'var(--text-primary)' }}>{verdict}</strong>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 min-w-[240px] flex-shrink-0">
                        <MiniAxis
                            label="Requirements"
                            value={`${requirementsMetEffective}/${requirementsTotal}`}
                            pct={requirementsTotal === 0 ? 0 : (requirementsMetEffective / requirementsTotal) * 100}
                        />
                        <MiniAxis
                            label="Profile strength"
                            value={
                                componentScoreEffective.max === 0
                                    ? '—'
                                    : `${componentScoreEffective.total}/${componentScoreEffective.max}`
                            }
                            pct={
                                componentScoreEffective.max === 0
                                    ? 0
                                    : (componentScoreEffective.total / componentScoreEffective.max) * 100
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Hard requirements */}
            {requirements.length > 0 && (
                <>
                    <SectionHeader icon={CheckSquare}>Hard requirements · Pass / Fail</SectionHeader>
                    {requirements.map((r) => (
                        <div
                            key={r.criterionId}
                            className="px-5 py-3.5"
                            style={{ borderBottom: '1px solid var(--border-muted)' }}
                            data-editable-score-host
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div
                                        className="text-[14px] font-medium leading-snug"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        {r.label}
                                    </div>
                                    {r.justification && (
                                        <div
                                            className="text-[13px] mt-1.5 leading-relaxed"
                                            style={{ color: 'var(--text-secondary)' }}
                                        >
                                            {r.justification}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0">
                                    <EditableScore
                                        reportKind="cv_screening"
                                        resultId={result._id}
                                        componentId={r.criterionId}
                                        componentLabel={r.label}
                                        scale={{ min: 0, max: 1 }}
                                        binary
                                        aiScore={r.isFulfilled ? 1 : 0}
                                        aiJustification={r.justification}
                                        edits={result.reviewerFeedback}
                                        size="sm"
                                        jobId={jobId}
                                        taskId={taskId}
                                        onEdited={onEdited}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* Profile strength components */}
            {components.length > 0 && (
                <>
                    <SectionHeader icon={AlignJustify}>Profile strength components</SectionHeader>
                    {components.map((c) => (
                        <div
                            key={c.componentId}
                            className="px-5 py-3.5"
                            style={{ borderBottom: '1px solid var(--border-muted)' }}
                            data-editable-score-host
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div
                                        className="text-[14px] font-medium leading-snug"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        {c.label}
                                    </div>
                                    {c.justification && (
                                        <div
                                            className="text-[13px] mt-1.5 leading-relaxed"
                                            style={{ color: 'var(--text-secondary)' }}
                                        >
                                            {c.justification}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0">
                                    <EditableScore
                                        reportKind="cv_screening"
                                        resultId={result._id}
                                        componentId={c.componentId}
                                        componentLabel={c.label}
                                        scale={{
                                            min: 1,
                                            max: c.scoreMax,
                                            labels:
                                                c.scoreMax === 5 ? RANK_SCALE_LABELS : undefined,
                                        }}
                                        aiScore={c.score}
                                        aiJustification={c.justification}
                                        edits={result.reviewerFeedback}
                                        size="md"
                                        jobId={jobId}
                                        taskId={taskId}
                                        onEdited={onEdited}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {requirements.length === 0 && components.length === 0 && (
                <div
                    className="px-5 py-12 text-center text-[13px]"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    <FileText
                        size={20}
                        strokeWidth={1.5}
                        style={{ display: 'inline', marginBottom: 8, color: 'var(--text-quaternary)' }}
                    />
                    <div>No criteria or components on this report yet.</div>
                </div>
            )}
        </div>
    );
}

function MiniAxis({ label, value, pct }: { label: string; value: string; pct: number }) {
    const color =
        pct >= 80 ? 'var(--fg-success)' :
        pct >= 60 ? 'var(--fg-info)' :
        pct >= 40 ? 'var(--fg-warning)' :
        'var(--fg-danger)';
    return (
        <div
            className="rounded-lg px-3 py-2.5"
            style={{ background: 'var(--surface-secondary)', border: '1px solid var(--border-default)' }}
        >
            <div className="flex items-baseline justify-between gap-2">
                <span
                    className="text-[10.5px] font-semibold uppercase tracking-[0.08em]"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    {label}
                </span>
                <span
                    className="text-[14px] font-medium tabular-nums"
                    style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-serif)' }}
                >
                    {value}
                </span>
            </div>
            <div
                className="h-1.5 rounded-full overflow-hidden mt-2"
                style={{ background: 'var(--surface-tertiary)' }}
            >
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: 'width 200ms' }} />
            </div>
        </div>
    );
}

function SectionHeader({
    children,
    icon: Icon,
}: {
    children: React.ReactNode;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}) {
    return (
        <div
            className="px-5 py-2 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em]"
            style={{
                color: 'var(--text-tertiary)',
                background: 'var(--surface-secondary)',
                borderBottom: '1px solid var(--border-muted)',
            }}
        >
            <Icon size={11} strokeWidth={2} />
            {children}
        </div>
    );
}
